/**
 * Structured stage-timing telemetry for initial workspace hydration (#240).
 *
 * Separates blocking critical-path stages (`auth_ready`, `company_scope_ready`,
 * `site_list_summary_ready`, `dashboard_first_render`) from non-blocking
 * background enrichment (`optional_member_directory_loaded`,
 * `background_composite_hydration_completed`).
 */

export type WorkspaceLoadStage =
  | 'auth_ready'
  | 'company_scope_ready'
  | 'site_list_summary_ready'
  | 'dashboard_first_render'
  | 'optional_member_directory_loaded'
  | 'background_composite_hydration_completed';

export interface WorkspaceLoadStageEvent {
  stage: WorkspaceLoadStage;
  timestampMs: number;
  elapsedSinceAuthReadyMs: number;
  metadata?: Record<string, unknown>;
}

export interface WorkspaceLoadTelemetrySnapshot {
  authReadyAtMs: number | null;
  stages: Partial<Record<WorkspaceLoadStage, WorkspaceLoadStageEvent>>;
  orderedEvents: WorkspaceLoadStageEvent[];
}

declare global {
  interface Window {
    __DATACRATE_WORKSPACE_LOAD_TIMINGS__?: WorkspaceLoadTelemetrySnapshot;
  }
}

function nowMs(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return Math.round(performance.now() * 100) / 100;
  }
  return Date.now();
}

let state: WorkspaceLoadTelemetrySnapshot = {
  authReadyAtMs: null,
  stages: {},
  orderedEvents: [],
};

function publishToWindow(): void {
  if (typeof window !== 'undefined') {
    window.__DATACRATE_WORKSPACE_LOAD_TIMINGS__ = {
      authReadyAtMs: state.authReadyAtMs,
      stages: { ...state.stages },
      orderedEvents: [...state.orderedEvents],
    };
  }
}

export function resetWorkspaceLoadTimings(startedAtMs?: number): void {
  const anchor = startedAtMs ?? null;
  state = {
    authReadyAtMs: anchor,
    stages: {},
    orderedEvents: [],
  };
  publishToWindow();
}

export function recordWorkspaceLoadStage(
  stage: WorkspaceLoadStage,
  metadata?: Record<string, unknown>,
  timestampOverrideMs?: number,
): WorkspaceLoadStageEvent {
  const timestampMs = timestampOverrideMs ?? nowMs();
  if (stage === 'auth_ready' || state.authReadyAtMs === null) {
    state.authReadyAtMs = timestampMs;
  }
  const elapsedSinceAuthReadyMs = Math.max(
    0,
    Math.round((timestampMs - (state.authReadyAtMs ?? timestampMs)) * 100) / 100,
  );
  const event: WorkspaceLoadStageEvent = {
    stage,
    timestampMs,
    elapsedSinceAuthReadyMs,
    ...(metadata ? { metadata } : {}),
  };
  state.stages[stage] = event;
  const existingIdx = state.orderedEvents.findIndex((item) => item.stage === stage);
  if (existingIdx >= 0) {
    state.orderedEvents[existingIdx] = event;
  } else {
    state.orderedEvents.push(event);
  }
  publishToWindow();
  return event;
}

export function getWorkspaceLoadTimings(): WorkspaceLoadTelemetrySnapshot {
  return {
    authReadyAtMs: state.authReadyAtMs,
    stages: { ...state.stages },
    orderedEvents: [...state.orderedEvents],
  };
}
